/**
 * EventPreviewPage — Renderiza un evento accedido vía preview_token.
 * (Mismo concepto que NewsPreviewPage — ver ese archivo para detalles.)
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Eye, Loader2, AlertCircle, Calendar, MapPin, ExternalLink } from 'lucide-react';
import { api, absoluteUrl } from '../lib/api';
import { normalizeHtml } from '../lib/htmlText';
import RichContentBlocks from '../components/RichContentBlocks';

const STATUS_LABEL = {
  draft:        { label: 'Borrador',     classes: 'bg-gray-100 text-gray-800 border-gray-300' },
  private_link: { label: 'Link privado', classes: 'bg-amber-50 text-amber-800 border-amber-200' },
  public:       { label: 'Público',      classes: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
};

export default function EventPreviewPage() {
  const { token } = useParams();
  const [item, setItem] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    api.get(`/events/preview/${token}`)
      .then(({ data }) => { if (alive) setItem(data); })
      .catch((e) => { if (alive) setError(e.response?.data?.detail || 'No se pudo cargar la vista previa'); });
    return () => { alive = false; };
  }, [token]);

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-8" data-testid="event-preview-error">
        <div className="text-center max-w-md">
          <AlertCircle size={48} className="text-rose-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-charcoal-800 mb-2">No se encontró la vista previa</h1>
          <p className="text-sm text-charcoal-500">{error}</p>
        </div>
      </div>
    );
  }
  if (!item) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="event-preview-loading">
        <Loader2 size={28} className="animate-spin text-cmic-500" />
      </div>
    );
  }

  const status = item.status || 'public';
  const meta = STATUS_LABEL[status] || STATUS_LABEL.public;
  const eventDate = item.event_date ? new Date(item.event_date) : null;

  return (
    <div data-testid="event-preview-page">
      {status !== 'public' && (
        <div className={`border-y px-5 py-2.5 text-center text-xs font-medium ${meta.classes}`} data-testid="event-preview-status-banner">
          <Eye size={12} className="inline-block mr-1" />
          Estás viendo este evento como <strong>{meta.label.toLowerCase()}</strong>.
          Cualquier persona con este enlace puede verlo, pero no aparece en el sitio público.
        </div>
      )}

      <article className="max-w-3xl mx-auto px-5 sm:px-8 py-10 break-words">
        {item.image_url && (
          <div className="rounded-2xl overflow-hidden mb-8 bg-gray-100">
            <img src={absoluteUrl(item.image_url)} alt={item.title} className="w-full h-auto max-h-[480px] object-cover" />
          </div>
        )}
        <h1 className="text-3xl sm:text-4xl font-bold text-charcoal-900 leading-tight break-words">{item.title}</h1>
        <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-charcoal-500">
          {eventDate && (
            <span className="inline-flex items-center gap-1.5">
              <Calendar size={14} className="text-cmic-500" />
              {eventDate.toLocaleString('es-MX', { dateStyle: 'long', timeStyle: 'short' })}
            </span>
          )}
          {item.location && (
            <span className="inline-flex items-center gap-1.5">
              <MapPin size={14} className="text-cmic-500" />
              {item.location}
            </span>
          )}
        </div>
        {item.description && (
          <div
            className="prose prose-charcoal max-w-none prose-headings:text-charcoal-800 prose-a:text-cmic-600 prose-strong:text-charcoal-800 prose-img:rounded-xl prose-img:max-w-full mt-6 break-words"
            dangerouslySetInnerHTML={{ __html: normalizeHtml(item.description) }}
          />
        )}
        <RichContentBlocks blocks={item.content_blocks} />
        {item.registration_url && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <a
              href={item.registration_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-cmic-500 text-white px-5 py-3 rounded-lg font-semibold hover:bg-cmic-600"
            >
              Más información <ExternalLink size={14} />
            </a>
          </div>
        )}
      </article>
    </div>
  );
}
